<template>
  <base-list-view
    @state-scroll="scrollList"
  >
    <template v-slot:filter-slot>
      <div class="exhibitor-filter-line catalog-search-line">
        <button
          v-if="filters.length"
          @click="showFilter"
          :class="{ 'is-applied': isFiltered }"
          class="up-btn-line-icon"
          :aria-label="$t('shared.filters.btn_open')"
        >
          <i class="exu-filter-funnel-02"/>
        </button>
        <up-input-filter
          :startValue="searchInput"
          @onSearch="(e) => startSearch(e)"
        />
      </div>
    </template>
    <template
      v-if="isShowFilter"
      v-slot:aside-filter-slot
    >
      <filters-view
        :filters="filters"
        :selectedFilters="filtersState"
        :levelTitles="filterLevelTitles"
        @update="applyFilters"
      />
    </template>
    <div class="exhibitor-list">
      <pre-loader v-if="isLoadingPrev"/>
      <div
        v-for="exhibitor in exhibitorsList"
        :key="exhibitor.id"
        :id="'exhibitor-' + exhibitor.id"
        class="exhibitor-item"
        @click="saveScrollContext(exhibitor)"
        @keydown.enter="saveScrollContext(exhibitor)"
      >
        <exhibitor-card
          :exhibitor-id="exhibitor.id"
          :logo="exhibitor.logo"
          :company="exhibitor.name"
          :stand="exhibitor.stand"
          :marked="!!getMarkedCompanyByID(exhibitor.id)"
          @marking="markingCompany"
        />
      </div>
      <system-card
        v-if="systemMessage"
        :system-message="systemMessage"
        :cbFunc="repeatGetPage"
      />
      <pre-loader v-if="isLoading && !isLoadingPrev"/>
    </div>
  </base-list-view>
</template>
<script>
import { DateTime } from 'luxon';
import appBaseNav from '@/components/mixins/appBaseNav';
import exhibitors from '@/components/mixins/methodsExhibitors';
import filters from '@/components/mixins/api/apiMethodsCatalogFilters';
import banners from '@/components/mixins/methodsBanners';
import listScrollMixin from '@/components/mixins/viewers/listScrollMixin';

import baseListView from '@/components/shared/baseListView.vue';
import SystemCard from '@/components/shared/upCard/SystemCard.vue';
import ExhibitorCard from '@/components/shared/upCard/ExhibitorCard.vue';
import FiltersView from '@/components/shared/asideMenu/asideFilters.vue';

export default {
  name: 'ListExhibitorsView',
  components: {
    baseListView, SystemCard, ExhibitorCard, FiltersView,
  },
  props: {
    launchDownload: {
      type: Boolean,
    },
    locale: {
      type: String,
    },
    page: {
      type: String,
      default: null,
    },
  },
  data() {
    const savedRaw = sessionStorage.getItem('exhibitorScrollCtx');
    const saved = savedRaw ? JSON.parse(savedRaw) : null;
    if (saved) sessionStorage.removeItem('exhibitorScrollCtx');
    const startPage = saved ? saved.firstPage : (parseInt(this.page, 10) || 1);
    return {
      exhibitorsList: [],
      markedExhibitors: [],
      filters: [],
      isLoading: true,
      firstPage: startPage,
      curPage: startPage,
      countPage: null,
      searchInput: saved?.search || null,

      isFiltered: !!saved?.isFiltered,
      filtersState: saved?.filtersState || {},
      filterParams: saved?.filterParams || null,

      isShowFilter: false,
      isLoadingPrev: false,
      isLastDownloaded: false,
      systemMessage: null,
      scrollTargetId: saved ? saved.targetId : null,
    };
  },
  mixins: [appBaseNav, exhibitors, banners, filters, listScrollMixin],
  computed: {
    // имён вложенных уровней конфиг не отдаёт — подписи для салонов задаём здесь
    filterLevelTitles() {
      return {
        category: [
          this.$t('exhibitorsList.filter_level_group'),
          this.$t('exhibitorsList.filter_level_section'),
        ],
      };
    },
  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('exhibitorsList.header_title'));
    this.loadMarkedExhibitors();
    Promise.all([
      this.getBanner('exhibitor', this.$store.getters.getBannerExhibitorTs),
      // конфиг фильтров не должен ронять каталог: не отдался — работаем без него
      this.getFilters('exhibitor').catch(() => []),
    ])
      .then(([
        banner,
        filter,
      ]) => {
        this.isLoading = false;
        if (banner && Object.keys(banner).length) {
          this.$emit('showBanner', banner);
          const nowMilis = DateTime.now().toMillis();
          localStorage.setItem('bannerExhibitorTs', nowMilis);
          this.$store.commit('setBannerExhibitorTs', nowMilis);
        }

        if (Array.isArray(filter)) {
          this.filters = [...filter];
        }

        this.getPage();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  },
  methods: {
    // Фильтр берём из состояния, а не из аргумента: следующую страницу
    // listScrollMixin запрашивает через getPage() без параметров, и вторая
    // страница отфильтрованного списка приходила без фильтра.
    getPage() {
      if (this.countPage === null || this.curPage <= this.countPage) {
        this.isLoading = true;

        return this.getExhibitorList(this.curPage, this.searchInput, this.filterParams)
          .then((data) => {
            this.isLoading = false;
            this.$emit('turnOff', {
              stateDownload: false,
              stateCheckInbox: false,
            });
            if (data?.results && Array.isArray(data.results)) {
              const tagged = data.results.map((e) => ({ ...e, sourcePage: data.page }));
              this.exhibitorsList = [...this.exhibitorsList, ...tagged];
              this.countPage = data.num_pages;
              this.curPage = data.page;
              this.isLastDownloaded = true;
              this.systemMessage = null;
              this.$nextTick(() => {
                if (this.scrollTargetId) {
                  this.tryScrollToTarget();
                  this.loadNeighbourPages();
                } else {
                  this.checkScrollBoundaries();
                }
              });
            } else {
              this.isLastDownloaded = false;
              this.systemMessage = this.$t('system.bad_connect');
            }
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('getPage()::', error);
          });
      }
      return Promise.resolve();
    },
    // После возврата из профиля в списке одна страница. Если карточек на ней
    // мало, прокручивать нечего и подгрузка по скроллу не сработает, поэтому
    // соседей грузим сразу: сначала верхнюю, её вставка держит позицию, потом нижнюю.
    loadNeighbourPages() {
      this.getPrevPage().then(() => {
        if (this.curPage >= this.countPage) return null;
        this.curPage += 1;
        return this.getPage();
      });
    },
    getPrevPage() {
      if (this.firstPage <= 1) return Promise.resolve();

      const scrollEl = document.getElementById('content__wrapper');
      const listEl = document.getElementById('listView');

      const heightBeforeLoader = listEl.offsetHeight;
      const scrollBeforeLoader = scrollEl.scrollTop;

      this.isLoading = true;
      this.isLoadingPrev = true;

      this.$nextTick(() => {
        this.adjustScroll(scrollBeforeLoader, heightBeforeLoader);
      });

      return this.getExhibitorList(this.firstPage - 1, this.searchInput, this.filterParams)
        .then((data) => {
          if (data?.results && Array.isArray(data.results)) {
            const heightBeforeData = listEl.offsetHeight;
            const scrollBeforeData = scrollEl.scrollTop;

            this.isLoading = false;
            this.isLoadingPrev = false;
            const tagged = data.results.map((e) => ({ ...e, sourcePage: data.page }));
            this.exhibitorsList = [...tagged, ...this.exhibitorsList];
            this.firstPage -= 1;
            this.systemMessage = null;

            this.$nextTick(() => {
              this.adjustScroll(scrollBeforeData, heightBeforeData);
            });
          } else {
            this.isLoading = false;
            this.isLoadingPrev = false;
            this.systemMessage = this.$t('system.bad_connect');
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('getPrevPage()::', error);
          this.isLoading = false;
          this.isLoadingPrev = false;
        });
    },
    loadMarkedExhibitors() {
      const exhID = this.$store.getters.getExhId;
      const markedList = localStorage.getItem(`exh-${exhID}-marked`);
      if (markedList === null) {
        localStorage.setItem(`exh-${exhID}-marked`, JSON.stringify([]));
      } else {
        JSON.parse(markedList).forEach((item) => {
          if (item.type === 'exhibitors') {
            this.markedExhibitors.push(item);
          }
        });
      }
    },
    saveMarkedExhibitors() {
      const exhID = this.$store.getters.getExhId;
      localStorage.setItem(`exh-${exhID}-marked`, JSON.stringify(this.markedExhibitors));
    },
    showFilter() {
      this.isShowFilter = !this.isShowFilter;
    },
    markingCompany(item) {
      const company = this.exhibitorsList.find((companyItem) => companyItem.id === item);
      const markedCompany = this.markedExhibitors
        .findIndex((companyItem) => companyItem.id === item);

      if (markedCompany === -1) {
        this.markedExhibitors.push({
          type: 'exhibitors',
          id: company.id,
        });
      } else {
        this.markedExhibitors.splice(markedCompany, 1);
      }

      this.saveMarkedExhibitors();
    },
    getCompanyByID(companyID) {
      return this.exhibitorsList.find((company) => company.id === companyID);
    },
    getMarkedCompanyByID(companyID) {
      return this.markedExhibitors.find((company) => company.id === companyID);
    },
    // Панель ведает только фильтрами: строку поиска над списком она не
    // трогает, поиск чистится крестиком в самом поле.
    applyFilters(params) {
      this.isShowFilter = false;

      if (params !== null && params.doFilter) {
        this.isFiltered = !!Object.keys(params.filters).length;
        this.filtersState = params.filtersState;
        this.filterParams = this.isFiltered
          ? { filter: JSON.stringify(params.filters) }
          : null;
        this.resetList();
        this.getPage();
      } else if (this.isFiltered) {
        this.isFiltered = false;
        this.filtersState = {};
        this.filterParams = null;
        this.resetList();
        this.getPage();
      }
    },
    startSearch(data) {
      this.searchInput = data;
      this.resetList();
      this.getPage();
    },
    resetList() {
      this.firstPage = 1;
      this.curPage = 1;
      this.countPage = null;
      this.exhibitorsList = [];
    },
    // Возврат из профиля должен поднять тот же список на той же карточке:
    // вместе с позицией запоминаем поиск и фильтры.
    saveScrollContext(exhibitor) {
      sessionStorage.setItem('exhibitorScrollCtx', JSON.stringify({
        targetId: `exhibitor-${exhibitor.id}`,
        firstPage: exhibitor.sourcePage || 1,
        search: this.searchInput,
        isFiltered: this.isFiltered,
        filtersState: this.filtersState,
        filterParams: this.filterParams,
      }));
    },
    tryScrollToTarget() {
      if (!this.scrollTargetId) return;
      const el = document.getElementById(this.scrollTargetId);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        this.scrollTargetId = null;
      }
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/style/variables.scss';

// Карточка под строкой поиска — та же, что у участников, но без ленты
// вкладок под ней: карточка замыкается сама, поэтому скруглены все четыре
// угла. Сама строка (сцепка воронки, поля и лупы) описана в upInputFilter.
.exhibitor-filter-line {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  padding: 8px;
  background: $color-base;
  border-radius: 16px;
}
.exhibitor {
  &-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  &-list &-item {
    flex: 1 1 140px;
    max-width: calc(50% - 6px);
    @media (min-width: 480px) {
      flex: 1 1 180px;
      max-width: calc(33.333% - 6px);
    }

    & .upCard {
      border-radius: 10px;
      padding: 10px;
      height: 203px;
    }
  }
}
</style>
